// Context provider for shared report state
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useReportData } from './hooks/useReportData.js';
import { usePhotoManagement } from './hooks/usePhotoManagement.js';
import { useReportGeneration } from './hooks/useReportGeneration.js';
import { PDFGenerator } from './generation/PDFGenerator.js';

const ReportsContext = createContext();

export const useReports = () => {
  const context = useContext(ReportsContext);
  if (!context) {
    throw new Error('useReports must be used within a ReportsProvider');
  }
  return context;
};

export const ReportsProvider = ({ children, defaultReportType = '' }) => {

  // Stub functions for page numbering (actual numbering done via DOM manipulation in preview modal)
  const updateSectionPageCount = useCallback(() => {}, []);
  const getGlobalPageNumber = useCallback((sectionId, localPage) => localPage, []);
  const totalPages = null;
  const sectionPageOffsets = {};

  // Add error handling for hooks
  let reportData, photoData, generationData;
  
  try {
    reportData = useReportData(defaultReportType);
    photoData = usePhotoManagement();
    generationData = useReportGeneration();
  } catch (error) {
    console.error('Error initializing report hooks:', error);
    return <div>Error initializing reports. Please refresh the page.</div>;
  }

  // Fetch initial data on mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Fetch sites with error handling
        const sites = await generationData.fetchSites();
        if (sites && Array.isArray(sites)) {
          reportData.setSites(sites);
        } else {
          reportData.setSites([]);
        }

        // Fetch organization settings with error handling
        const orgSettings = await generationData.fetchOrganizationSettings();
        if (orgSettings && typeof orgSettings === 'object') {
          reportData.setOrganizationSettings(orgSettings);
        } else {
          reportData.setOrganizationSettings({
            companyName: 'G4 Design & Build'
          });
        }
      } catch (error) {
        console.error('Error initializing report data:', error);
        // Set safe defaults
        reportData.setSites([]);
        reportData.setOrganizationSettings({
          companyName: 'G4 Design & Build'
        });
      }
    };

    initializeData();
  }, []);

  // Enhanced functions that combine multiple hooks
  const enhancedFunctions = {
    // Enhanced save report that includes photo data
    saveReportWithPhotos: async () => {
      const currentState = reportData.getCurrentReportState(
        photoData.selectedPhotos,
        photoData.photoLabels,
        photoData.importedPhotos
      );
      
      // Update the saved state to include photo data
      reportData.setSavedReportState(currentState);
      return reportData.saveReport();
    },

    // Enhanced generate report that includes all data
    generateFullReport: async () => {
      return generationData.generateComprehensiveReport(
        reportData.reportData,
        reportData.reportSections,
        reportData.selectedPeriod,
        reportData.selectedSite,
        photoData.selectedPhotos,
        photoData.photoLabels,
        reportData.currentReportId
      );
    },

    // Load photos for current period
    loadPhotosForCurrentPeriod: async () => {
      if (reportData.selectedPeriod.startDate && reportData.selectedPeriod.endDate) {
        return await photoData.importPhotosFromPeriod(
          reportData.selectedPeriod.startDate,
          reportData.selectedPeriod.endDate
        );
      }
    },

    // Check if required fields are complete
    checkRequiredFields: () => {
      return (
        reportData.reportData.title &&
        reportData.selectedPeriod.startDate &&
        reportData.selectedPeriod.endDate &&
        reportData.selectedSite
      );
    },

    // Get tab completion status
    getTabCompletionStatus: (tabId) => {
      // Import validation function
      const { reportValidation } = require('./utils/reportValidation.js');
      return reportValidation.validateTabCompletion(
        tabId,
        reportData.reportData,
        reportData.reportSections,
        reportData.selectedPeriod,
        reportData.selectedSite,
        photoData.selectedPhotos,
        photoData.photoLabels,
        photoData.importedPhotos
      );
    },

    // Native Browser PDF Generation - exact styling with selectable text
    downloadReportPDF: async (onProgress = null) => {
      try {
        const { NativePDFGenerator } = await import('./generation/NativePDFGenerator');
        
        return await NativePDFGenerator.downloadNativePDF(
          generationData.reportPreviewRef,
          reportData.reportData,
          reportData.sites.find(site => site.id === reportData.selectedSite),
          onProgress
        );
      } catch (nativeError) {
        console.warn('❌ Native PDF generation failed, trying simple print method:', nativeError);
        
        try {
          const { NativePDFGenerator } = await import('./generation/NativePDFGenerator');
          
          return await NativePDFGenerator.downloadViaPrintCSS(
            generationData.reportPreviewRef,
            reportData.reportData,
            reportData.sites.find(site => site.id === reportData.selectedSite),
            onProgress
          );
        } catch (printError) {
          console.warn('❌ Print CSS method also failed, falling back to text-based:', printError);
          
          // Fallback to text-based method
          try {
            const { TextBasedPDFGenerator } = await import('./generation/TextBasedPDFGenerator');
            
            return await TextBasedPDFGenerator.downloadTextBasedPDF(
              generationData.reportPreviewRef,
              reportData.reportData,
              reportData.sites.find(site => site.id === reportData.selectedSite),
              onProgress
            );
          } catch (finalError) {
            console.warn('❌ All methods failed, using original fallback:', finalError);
            
            return PDFGenerator.downloadReportPDF(
              generationData.generatedReportData,
              generationData.reportPreviewRef,
              onProgress
            );
          }
        }
      }
    },

    // Debug functions for troubleshooting PDF generation
    debugPDFGeneration: async () => {
      try {
        const { PDFDebugger } = await import('./generation/PDFDebugger');
        console.log('🔍 Starting PDF generation debug...');
        
        // Debug the preview structure
        PDFDebugger.debugPreviewStructure(generationData.reportPreviewRef);
        
        // Test page navigation
        await PDFDebugger.testPageNavigation(generationData.reportPreviewRef);
        
        // Create a simple test PDF
        await PDFDebugger.createTestPDF(generationData.reportPreviewRef);
        
        console.log('✅ PDF debug complete - check console for details');
      } catch (error) {
        console.error('❌ PDF debug failed:', error);
      }
    },

    // Populate report with sample data (enhanced)
    populateReportWithSampleData: async () => {
      return generationData.populateReport(
        reportData.reportData,
        reportData.setReportData,
        reportData.setReportSections,
        reportData.setSelectedPeriod,
        reportData.setSelectedSite,
        reportData.sites,  // Pass the sites array from state
        photoData.selectedPhotos,
        photoData.setSelectedPhotos,
        photoData.setPhotoLabels,
        photoData.setImportedPhotos
      );
    },

    // Get all photos (utility function)
    getAllPhotos: () => {
      return photoData.getAllPhotos();
    }
  };

  const value = {
    // All report data
    ...reportData,
    
    // All photo data
    ...photoData,
    
    // All generation data
    ...generationData,
    
    // Enhanced functions
    ...enhancedFunctions,

    // Stub page numbering functions (actual numbering done via DOM in preview)
    updateSectionPageCount,
    getGlobalPageNumber,
    totalPages,
    sectionPageOffsets,

    // Direct access to PDF generator for component use
    PDFGenerator
  };

  return (
    <ReportsContext.Provider value={value}>
      {children}
    </ReportsContext.Provider>
  );
};